import React, { useState } from 'react';
import StorefrontAPI from 'utilities/storefrontApi';
import { SHELL, MAX, CARD, SUB_INPUT, BUTTON, formatMoney } from './styles';
import useBrand from './useBrand';
import StorefrontNav from './StorefrontNav';
import { ShellMessage, Row, Field } from './shellHelpers';

/**
 * Public order-tracking page. Shopper types their order number + the
 * email used at checkout — backend verifies the pair before returning
 * any order data so we don't leak orders to anyone with the number.
 */
export default function StorefrontTrackOrder() {
  const { brand, error } = useBrand();
  const [number, setNumber] = useState('');
  const [email, setEmail] = useState('');
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const accent = brand?.brand_color || '#422AFB';

  const submit = async (e) => {
    e.preventDefault();
    if (!brand?._id) return;
    setErr(''); setOrder(null); setBusy(true);
    try {
      const res = await StorefrontAPI.trackOrder(brand._id, {
        number: number.trim(),
        email: email.trim(),
      });
      setOrder(res);
    } catch (e2) {
      setErr(e2?.message || 'Could not find that order');
    } finally {
      setBusy(false);
    }
  };

  if (error) return <ShellMessage>{error}</ShellMessage>;
  if (!brand) return <ShellMessage>Loading…</ShellMessage>;

  return (
    <div style={SHELL}>
      <StorefrontNav brand={brand} />
      <div style={MAX}>
        <h1 style={{ fontSize: 28, margin: '8px 0 18px' }}>Track an order</h1>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 2fr)', gap: 24 }}>
          <form onSubmit={submit} style={CARD}>
            <div style={{ fontSize: 13, color: '#475569', marginBottom: 10 }}>
              Enter the order number from your confirmation email and the
              email you used at checkout.
            </div>
            <Field label='Order number'>
              <input
                style={SUB_INPUT}
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                placeholder='ORD-2026-00041' required
              />
            </Field>
            <Field label='Email used'>
              <input
                style={SUB_INPUT} type='email'
                value={email}
                onChange={(e) => setEmail(e.target.value)} required
              />
            </Field>
            {err && <div style={{ color: '#dc2626', fontSize: 13, marginTop: 6 }}>{err}</div>}
            <button type='submit' style={{ ...BUTTON(accent), width: '100%', marginTop: 10 }} disabled={busy}>
              {busy ? 'Looking up…' : 'Look up order'}
            </button>
          </form>

          <div>
            {!order ? (
              <div style={{ ...CARD, color: '#64748b' }}>
                Order details appear here after lookup.
              </div>
            ) : (
              <div style={CARD}>
                <div style={{ fontSize: 13, color: '#475569', marginBottom: 4 }}>
                  Order <strong>{order.number}</strong>
                </div>
                <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 12 }}>
                  Status: <span style={{ color: accent }}>{order.status}</span>
                </div>
                {(order.tracking_carrier || order.tracking_number) && (
                  <div style={{
                    padding: 10, background: '#f1f5f9', borderRadius: 8,
                    fontSize: 13, color: '#0f172a', marginBottom: 12,
                  }}>
                    Tracking: <strong>{order.tracking_carrier}</strong>{' '}
                    {order.tracking_number}
                  </div>
                )}
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <tbody>
                    {order.line_items?.map((li, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '6px 0' }}>
                          <div style={{ fontWeight: 600 }}>{li.product_name}</div>
                          {li.variant_name && (
                            <div style={{ color: '#64748b', fontSize: 12 }}>{li.variant_name}</div>
                          )}
                          <div style={{ color: '#64748b', fontSize: 12 }}>× {li.quantity}</div>
                        </td>
                        <td style={{ padding: '6px 0', textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {formatMoney(li.line_total, order.currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ borderTop: '1px solid #e5e7eb', marginTop: 10, paddingTop: 10 }}>
                  <Row label='Subtotal'>{formatMoney(order.subtotal, order.currency)}</Row>
                  {Number(order.discount_total || 0) > 0 && (
                    <Row label='Discount'>-{formatMoney(order.discount_total, order.currency)}</Row>
                  )}
                  {Number(order.shipping_total || 0) > 0 && (
                    <Row label='Shipping'>{formatMoney(order.shipping_total, order.currency)}</Row>
                  )}
                  {Number(order.tax_total || 0) > 0 && (
                    <Row label='Tax'>{formatMoney(order.tax_total, order.currency)}</Row>
                  )}
                  <Row label='Total'><strong>{formatMoney(order.grand_total, order.currency)}</strong></Row>
                </div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 10 }}>
                  Ships to {order.shipping_address?.city || ''}
                  {order.shipping_address?.region ? `, ${order.shipping_address.region}` : ''}
                  {order.shipping_address?.country ? `, ${order.shipping_address.country}` : ''}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
